import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConfirmProvider } from "@/context/ConfirmContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import EventsPage from "@/pages/EventsPage";
import EventDetail from "@/pages/EventDetail";
import WeddingsPage from "@/pages/WeddingsPage";
import WeddingDetail from "@/pages/WeddingDetail";
import TrialsPage from "@/pages/TrialsPage";
import TeamPage from "@/pages/TeamPage";
import ExpensesPage from "@/pages/ExpensesPage";
import InvitationsPage from "@/pages/InvitationsPage";
import AlertsPage from "@/pages/AlertsPage";
import StaffingPage from "@/pages/StaffingPage";
import CalendarPage from "@/pages/CalendarPage";
import AccessRequestsPage from "@/pages/AccessRequestsPage";
import QuotationStudioPage from "@/pages/QuotationStudioPage";
import WebsiteLeadsPage from "@/pages/WebsiteLeadsPage";
import PublicQuotePage from "@/pages/PublicQuotePage";
import EnquiryFormPage from "@/pages/EnquiryFormPage";
import ComingSoonPage from "@/pages/ComingSoonPage";
import AppLayout from "@/components/AppLayout";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="text-[#5C5652] text-xs uppercase tracking-[0.3em]">Loading</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Process the OAuth session_id (URL fragment) FIRST, before any protected route runs.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<ComingSoonPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/q/:publicId" element={<PublicQuotePage />} />
      <Route path="/enquiry" element={<EnquiryFormPage />} />
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/weddings" element={<ProtectedRoute adminOnly><WeddingsPage /></ProtectedRoute>} />
        <Route path="/quotations" element={<ProtectedRoute adminOnly><QuotationStudioPage /></ProtectedRoute>} />
        <Route path="/leads" element={<ProtectedRoute adminOnly><WebsiteLeadsPage /></ProtectedRoute>} />
        <Route path="/weddings/:id" element={<ProtectedRoute adminOnly><WeddingDetail /></ProtectedRoute>} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/invitations" element={<InvitationsPage />} />
        <Route path="/alerts" element={<ProtectedRoute adminOnly><AlertsPage /></ProtectedRoute>} />
        <Route path="/staffing" element={<ProtectedRoute adminOnly><StaffingPage /></ProtectedRoute>} />
        <Route path="/access-requests" element={<ProtectedRoute adminOnly><AccessRequestsPage /></ProtectedRoute>} />
        <Route path="/trials" element={<TrialsPage />} />
        <Route path="/expenses" element={<ExpensesPage />} />
        <Route
          path="/team"
          element={
            <ProtectedRoute adminOnly>
              <TeamPage />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}

export default App;
